import { useTranslation } from "react-i18next"

import { mapTypenameToResourceType } from "shared/api/serializer/mapTypenameToAPIType"
import { raiseToast } from "shared/lib/toast/raiseToast"
import { ApiResponse } from "shared/types/ApiResponse"
import { AttachmentLinkFormData } from "shared/types/AttachmentLinkFormData"
import {
  FloorTypename,
  ProductPositionTypename,
  ProjectTypename,
  RoomTypename
} from "shared/types/Project"
import { ToastType } from "shared/types/Toast"

import { CreateAttachmentLinkMutation } from "../graphql/mutations.generated"
import { useAttachmentsLazyQuery } from "../graphql/project-attachments.queries.generated"
import { useCreateAttachmentLink } from "./useCreateAttachmentLink"
import { useDeleteAttachmentLink } from "./useDeleteAttachmentLink"

type AttachmentResourceTypename =
  | typeof ProjectTypename
  | typeof FloorTypename
  | typeof RoomTypename
  | typeof ProductPositionTypename

interface UpdateAttachmentLinksArgs {
  id: string
  resourceType: AttachmentResourceTypename
  attachmentLinks: AttachmentLinkFormData
  options?: { supressToast?: boolean }
}

type UpdateAttachmentLinksData = {
  createdLinks: CreateAttachmentLinkMutation["attachment"][]
  deletedIds: string[]
}

export function useUpdateAttachmentLinks() {
  const { t } = useTranslation()
  const [fetchAttachments] = useAttachmentsLazyQuery()
  const [createAttachmentLink] = useCreateAttachmentLink()
  const [deleteAttachmentLink] = useDeleteAttachmentLink()

  async function updateAttachmentLinks({
    id,
    resourceType,
    attachmentLinks,
    options
  }: UpdateAttachmentLinksArgs): Promise<
    ApiResponse<UpdateAttachmentLinksData>
  > {
    const { data: attachmentsData, error } = await fetchAttachments({
      variables: { id, type: mapTypenameToResourceType(resourceType) ?? "" }
    })

    if (error) {
      return { data: null, errors: [{ message: error.message }] }
    }

    const existingLinks = new Set(
      attachmentsData?.attachments?.map(a => a.id) ?? []
    )

    const toCreate: string[] = []
    const toDelete: string[] = []

    Object.entries(attachmentLinks).forEach(([attachmentId, hasLink]) => {
      if (hasLink && !existingLinks.has(attachmentId)) {
        toCreate.push(attachmentId)
      } else if (!hasLink && existingLinks.has(attachmentId)) {
        toDelete.push(attachmentId)
      }
    })

    const [createResults, deleteResults] = await Promise.all([
      Promise.all(
        toCreate.map(attachmentId =>
          createAttachmentLink({ id, resourceType, attachmentId })
        )
      ),
      Promise.all(
        toDelete.map(attachmentId =>
          deleteAttachmentLink({ id, resourceType, attachmentId })
        )
      )
    ])

    const createdLinks = createResults
      .filter(r => r.data !== null)
      .map(r => (r.data as NonNullable<(typeof r)["data"]>).attachment)

    const deletedIds = toDelete.filter(
      (_, i) => deleteResults[i].data?.deleteAttachmentLink.success
    )

    const errors = [
      ...createResults.flatMap(r => r.errors ?? []),
      ...deleteResults.flatMap(r => r.errors ?? [])
    ]

    if (errors.length > 0 && !options?.supressToast) {
      raiseToast({
        message: t("Attachments.UpdateLinksError"),
        type: ToastType.ERROR
      })
    }

    return {
      data: { createdLinks, deletedIds },
      ...(errors.length > 0 ? { errors } : {})
    }
  }

  return updateAttachmentLinks
}
